import React, { useState } from 'react';
import { X, Copy, Check, Share2, Link as LinkIcon, Mail, Linkedin, Twitter } from 'lucide-react';
import { withBackend } from '../utils/api';

const ShareReportModal = ({ isOpen, onClose, analysisData, presentationName }) => {
  const [isCreating, setIsCreating] = useState(false);
  const [shareLink, setShareLink] = useState(null);
  const [copied, setCopied] = useState(false);
  const [expirationDays, setExpirationDays] = useState(30);
  const [error, setError] = useState(null);

  if (!isOpen) return null;

  const createShareLink = async () => {
    setIsCreating(true);
    setError(null);

    try {
      const response = await fetch(withBackend('/api/share-report'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          analysis_data: analysisData,
          presentation_name: presentationName || 'Untitled Presentation',
          expiration_days: expirationDays,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to create share link');
      }

      const data = await response.json();

      if (data.success) {
        setShareLink(data.url);
      } else {
        throw new Error(data.error || 'Failed to create share link');
      }
    } catch (err) {
      console.error('Error creating share link:', err);
      setError(err.message);
    } finally {
      setIsCreating(false);
    }
  };

  const copyToClipboard = async () => {
    if (shareLink) {
      try {
        await navigator.clipboard.writeText(shareLink);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch (err) {
        console.error('Failed to copy:', err);
      }
    }
  };

  const shareViaEmail = () => {
    const subject = encodeURIComponent(`Check out this presentation analysis: ${presentationName}`);
    const body = encodeURIComponent(`I thought you might find this presentation analysis interesting:\n\n${shareLink}`);
    window.open(`mailto:?subject=${subject}&body=${body}`, '_blank');
  };

  const shareViaLinkedIn = () => {
    const url = encodeURIComponent(shareLink);
    window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${url}`, '_blank');
  };

  const shareViaTwitter = () => {
    const text = encodeURIComponent(`Check out this presentation analysis: ${presentationName}`);
    const url = encodeURIComponent(shareLink);
    window.open(`https://twitter.com/intent/tweet?text=${text}&url=${url}`, '_blank');
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Share2 className="w-5 h-5 text-blue-500" />
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              Share Report
            </h2>
          </div>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        {!shareLink ? (
          <div className="space-y-4">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Create a shareable link for this analysis report. Anyone with the link can view the results.
            </p>

            {/* Expiration Options */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Link expires after:
              </label>
              <div className="grid grid-cols-3 gap-2">
                {[
                  { label: '7 days', value: 7 },
                  { label: '30 days', value: 30 },
                  { label: '90 days', value: 90 },
                ].map((option) => (
                  <button
                    key={option.value}
                    onClick={() => setExpirationDays(option.value)}
                    className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                      expirationDays === option.value
                        ? 'bg-blue-500 text-white'
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>

            {error && (
              <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
              </div>
            )}

            {/* Create Button */}
            <button
              onClick={createShareLink}
              disabled={isCreating}
              className="w-full px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isCreating ? 'Creating link...' : 'Create Share Link'}
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Your shareable link is ready! It will expire in {expirationDays} days.
            </p>

            {/* Share Link Display */}
            <div className="flex items-center gap-2 p-3 bg-gray-50 dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700">
              <LinkIcon className="w-4 h-4 text-gray-400 flex-shrink-0" />
              <input
                type="text"
                value={shareLink}
                readOnly
                className="flex-1 bg-transparent text-sm text-gray-700 dark:text-gray-300 outline-none"
              />
              <button
                onClick={copyToClipboard}
                className="p-2 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg transition-colors"
                title="Copy link"
              >
                {copied ? (
                  <Check className="w-4 h-4 text-green-500" />
                ) : (
                  <Copy className="w-4 h-4 text-gray-500" />
                )}
              </button>
            </div>

            {/* Social Share Buttons */}
            <div>
              <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Share via:
              </p>
              <div className="flex gap-2">
                <button
                  onClick={shareViaEmail}
                  className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-lg text-sm font-medium transition-colors"
                >
                  <Mail className="w-4 h-4" />
                  Email
                </button>
                <button
                  onClick={shareViaLinkedIn}
                  className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-[#0A66C2] hover:bg-[#004182] text-white rounded-lg text-sm font-medium transition-colors"
                >
                  <Linkedin className="w-4 h-4" />
                  LinkedIn
                </button>
                <button
                  onClick={shareViaTwitter}
                  className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-[#1DA1F2] hover:bg-[#1a8cd8] text-white rounded-lg text-sm font-medium transition-colors"
                >
                  <Twitter className="w-4 h-4" />
                  Twitter
                </button>
              </div>
            </div>

            {/* Done Button */}
            <button
              onClick={onClose}
              className="w-full px-4 py-2 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-lg font-medium transition-colors"
            >
              Done
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ShareReportModal;
